import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { appQueryClient } from '../appQueryClient';
import { ApiHandler } from '../handlers';
import { Representative } from '../models';

export const useRepresentativesQuery = (
    doneeId: number,
    userId?: number,
    disabled?: boolean,
) => {
    const query = useQuery({
        queryKey: [ApiHandler.instance.donees.getRepresentatives.name, doneeId],
        queryFn: async () => {
            const result = await ApiHandler.instance.donees.getRepresentatives(
                doneeId,
            );
            if (!result.success) {
                return [];
            }
            const data = result.response.data;
            if (!Array.isArray(data)) return [];
            // Active primary representative (Faith Leader) will be first in the list
            // Current user representative will be second if not active
            const sortedData = data.sort((a, b) => {
                if (a.representative) return -1;
                if (b.representative) return 1;
                if (a.officialId === userId) return -1;
                if (b.officialId === userId) return 1;
                return 0;
            });
            return sortedData;
        },
        enabled: !!doneeId && !disabled,
        refetchOnMount: 'always',
        refetchInterval: Infinity,
    });

    const addOrUpdateRepresentative = React.useCallback(
        (newRepresentative: Representative) => {
            if (!newRepresentative || newRepresentative.officialId == null) {
                return;
            }
            appQueryClient.setQueryData(
                [ApiHandler.instance.donees.getRepresentatives.name, doneeId],
                (oldData: any[] | undefined) => {
                    if (!oldData || oldData.length === 0)
                        return [newRepresentative];
                    const existingIndex = oldData.findIndex(
                        (rep) =>
                            rep.officialId === newRepresentative.officialId,
                    );
                    let filteredData = oldData;
                    if (existingIndex !== -1) {
                        filteredData = oldData.filter(
                            (_, idx) => idx !== existingIndex,
                        );
                    }
                    let updatedData;
                    if (newRepresentative.representative) {
                        updatedData = [
                            newRepresentative,
                            ...filteredData.map((rep) => ({
                                ...rep,
                                representative: false,
                            })),
                        ];
                    } else {
                        updatedData =
                            filteredData.length > 0
                                ? [
                                      filteredData[0],
                                      newRepresentative,
                                      ...filteredData.slice(1),
                                  ]
                                : [newRepresentative];
                    }
                    updatedData = updatedData.sort((a, b) => {
                        if (a.representative) return -1;
                        if (b.representative) return 1;
                        if (a.officialId === userId) return -1;
                        if (b.officialId === userId) return 1;
                        return 0;
                    });
                    return updatedData;
                },
            );
        },
        [doneeId, userId],
    );

    const clearActiveRepresentative = React.useCallback(() => {
        appQueryClient.setQueryData(
            [ApiHandler.instance.donees.getRepresentatives.name, doneeId],
            (oldData: any[] | undefined) => {
                if (!oldData || oldData.length === 0) return [];
                const existingIndex = oldData.findIndex(
                    (rep) => rep.representative,
                );
                let updatedData = oldData;
                if (existingIndex !== -1) {
                    updatedData = oldData.map((rep, idx) =>
                        idx === existingIndex
                            ? { ...rep, representative: false }
                            : rep,
                    );
                    updatedData = updatedData.sort((a, b) => {
                        if (a.representative) return -1;
                        if (b.representative) return 1;
                        if (a.officialId === userId) return -1;
                        if (b.officialId === userId) return 1;
                        return 0;
                    });
                }
                return updatedData;
            },
        );
    }, [doneeId, userId]);

    return { query, addOrUpdateRepresentative, clearActiveRepresentative };
};
